import * as React from 'react';
import { Gift, Loader2, Mail } from 'lucide-react';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { showGlobalModal } from 'core/components/GlobalModal';
import { PaginatedCardGrid } from 'core/components/PaginatedCardGrid';
import httpclient from 'libs/Axios';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';
import EventSetupCard from 'features/SocialHub/components/DesktopComponents/ListDetails/EventSetupCard';
import { DMButton, DMDiv, DMText } from '../components/Primitives';

type SendWishesTab = 'FESTIVAL' | 'EVENT';

type MasterEventItem = {
  id: number;
  title: string;
  description: string;
  startDate: string;
  type: SendWishesTab;
  status: string;
  bannerUrl: string;
  bannerImageUrl: string;
  isOpted: boolean;
  raw: any;
};

const tabConfig = {
  FESTIVAL: {
    label: 'Wishes',
    emptyText: 'No wishes found',
    icon: Gift,
  },
  EVENT: {
    label: 'Invitations',
    emptyText: 'No invitations found',
    icon: Mail,
  },
};

const mapMasterEvent = (item: any): MasterEventItem => ({
  id: Number(item?.MASTER_EVENT_ID || item?.master_event_id || item?.id || 0),
  title: item?.TITLE || item?.title || '-',
  description: item?.DESCRIPTION || item?.description || '',
  startDate: item?.START_DATE || item?.start_date || '',
  type:
    String(item?.TYPE || item?.type || 'EVENT').toUpperCase() === 'FESTIVAL'
      ? 'FESTIVAL'
      : 'EVENT',
  status: item?.STATUS || item?.status || '',
  bannerUrl: item?.BannerUrl || item?.BANNER_URL || item?.banner_url || '',
  bannerImageUrl:
    item?.Banner_img_url || item?.BANNER_IMG_URL || item?.banner_img_url || '',
  isOpted:
    item?.IS_OPTED === true ||
    item?.IS_OPTED === 1 ||
    String(item?.IS_OPTED || item?.is_opted || '').toLowerCase() === 'true',
  raw: item,
});

const SendWishesCardSkeleton = () => (
  <DMDiv
    className="h-[38vh] rounded-xl border border-solid bg-neutral-light p-3 shadow-md"
    aria-hidden="true"
  >
    <DMDiv className="h-[80%] w-full animate-pulse rounded-xl bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200" />
  </DMDiv>
);

const SendWishesCard = ({
  item,
  isSelected,
  onToggle,
}: {
  item: MasterEventItem;
  isSelected: boolean;
  onToggle: () => void;
}) => {
  return (
    <DMDiv className="h-[38vh]">
      <EventSetupCard
        isSelected={isSelected}
        onToggle={onToggle}
        showAnimatedImageLoader
        card={{
          ...item.raw,
          MASTER_EVENT_ID: item.id,
          TITLE: item.title,
          START_DATE: item.startDate,
          TYPE: item.type,
          BannerUrl: item.bannerUrl,
          BANNER_IMG_URL: item.bannerImageUrl,
        }}
      />
    </DMDiv>
  );
};

export function SendWishes() {
  const authData = useAuth();
  const [activeTab, setActiveTab] = React.useState<SendWishesTab>('FESTIVAL');
  const [eventRows, setEventRows] = React.useState<
    Record<SendWishesTab, MasterEventItem[]>
  >({
    FESTIVAL: [],
    EVENT: [],
  });
  const [eventTotals, setEventTotals] = React.useState<
    Record<SendWishesTab, number>
  >({
    FESTIVAL: 0,
    EVENT: 0,
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedEventIds, setSelectedEventIds] = React.useState<number[]>([]);
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const requestIdRef = React.useRef(0);
  const touchedSelectionIdsRef = React.useRef<Set<number>>(new Set());
  const eventRecordCacheRef = React.useRef<Map<number, MasterEventItem>>(
    new Map()
  );
  const loadedTabsRef = React.useRef<Set<SendWishesTab>>(new Set());
  const currentTabConfig = tabConfig[activeTab];
  const rows = eventRows[activeTab];
  const totalRows = eventTotals[activeTab];
  const areAllVisibleRowsSelected =
    rows.length > 0 && rows.every(item => selectedEventIds.includes(item.id));

  const requestEvents = React.useCallback(
    async (
      eventType: SendWishesTab,
      requestedPageIndex: number,
      requestedPageSize: number
    ) => {
      const response = await httpclient().get(
        `/master/masterEvent/list?page_size=${requestedPageSize}&page_index=${requestedPageIndex}&event_type=${eventType}`
      );
      const data = response?.data?.data || {};
      const items = Array.isArray(data?.['Master Events'])
        ? data['Master Events']
        : [];
      const total = Number(
        data?.Total ||
          data?.TOTAL ||
          data?.total ||
          data?.total_count ||
          data?.['Total Records'] ||
          items.length
      );

      return {
        items: items.map(mapMasterEvent) as MasterEventItem[],
        total,
      };
    },
    []
  );

  const fetchEvents = React.useCallback(
    async (eventType: SendWishesTab) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      const hasLoadedTab = loadedTabsRef.current.has(eventType);

      if (hasLoadedTab) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      try {
        const result = await requestEvents(eventType, pageIndex, pageSize);

        if (requestIdRef.current === requestId) {
          loadedTabsRef.current.add(eventType);
          result.items.forEach(item => {
            eventRecordCacheRef.current.set(item.id, item);
          });
          setEventRows(current => ({
            ...current,
            [eventType]: result.items,
          }));
          setEventTotals(current => ({
            ...current,
            [eventType]: result.total,
          }));
          setSelectedEventIds(current =>
            Array.from(
              new Set([
                ...current,
                ...result.items
                  .filter(
                    item =>
                      item.isOpted &&
                      !touchedSelectionIdsRef.current.has(item.id)
                  )
                  .map(item => item.id),
              ])
            )
          );
        }
      } catch (error: any) {
        if (requestIdRef.current === requestId) {
          showGlobalModal(
            getApiErrorMessage(
              error,
              `Unable to fetch ${tabConfig[eventType].label.toLowerCase()}`
            ),
            'error'
          );
          setEventRows(current => ({
            ...current,
            [eventType]: [],
          }));
          setEventTotals(current => ({
            ...current,
            [eventType]: 0,
          }));
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [pageIndex, pageSize, requestEvents]
  );

  React.useEffect(() => {
    httpclient().cancelAllRequests();
    void fetchEvents(activeTab);

    return () => {
      httpclient().cancelAllRequests();
    };
  }, [activeTab, fetchEvents]);

  const handleToggleCard = (itemId: number) => {
    touchedSelectionIdsRef.current.add(itemId);
    setSelectedEventIds(current =>
      current.includes(itemId)
        ? current.filter(id => id !== itemId)
        : [...current, itemId]
    );
  };

  const handleToggleAll = () => {
    const visibleIds = new Set(rows.map(item => item.id));
    rows.forEach(item => touchedSelectionIdsRef.current.add(item.id));
    setSelectedEventIds(current =>
      areAllVisibleRowsSelected
        ? current.filter(id => !visibleIds.has(id))
        : Array.from(new Set([...current, ...rows.map(item => item.id)]))
    );
  };

  const handleTabChange = (tab: SendWishesTab) => {
    if (tab === activeTab) {
      return;
    }

    requestIdRef.current += 1;
    loadedTabsRef.current.clear();
    touchedSelectionIdsRef.current.clear();
    eventRecordCacheRef.current.clear();
    setSelectedEventIds([]);
    setEventRows({
      FESTIVAL: [],
      EVENT: [],
    });
    setEventTotals({
      FESTIVAL: 0,
      EVENT: 0,
    });
    setIsRefreshing(false);
    setIsLoading(true);
    setPageIndex(1);
    setActiveTab(tab);
  };

  const handleSaveSchedule = async () => {
    setIsSaving(true);

    try {
      const optedEvents = selectedEventIds
        .map(itemId => eventRecordCacheRef.current.get(itemId))
        .filter((item): item is MasterEventItem => Boolean(item))
        .map(item => ({
          MASTER_EVENT_ID: item.id,
          EVENT_NAME: item.title,
          EVENT_DATE: item.startDate,
          EVENT_BANNER: item.bannerUrl || item.bannerImageUrl,
          EVENT_TYPE: item.type,
        }));

      await httpclient().post('/comm/branchEvent/sync', {
        BRANCH_ID: authData?.BRANCH_ID,
        ACADEMIC_YEAR_ID: authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_ID,
        OPTED_EVENTS: optedEvents,
      });

      touchedSelectionIdsRef.current.clear();
      showGlobalModal('Updated Successfully!');
      await fetchEvents(activeTab);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to save schedule'),
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-900"
    >
      <DMDiv className="mb-[2.4vh] shrink-0">
        <DigitalMarketingBreadcrumb
          className="mb-[1.6vh]"
          items={[{ label: 'Send Wishes' }]}
        />

        {/* <h1 className="text-[3.9vh] font-bold leading-tight text-gray-950">
                    Send Wishes
                </h1>
                <p className="mt-[0.6vh] text-[1.85vh] font-medium leading-tight text-gray-600">
                    Schedule festival wishes and event invitations for your audience.
                </p> */}
      </DMDiv>

      <DMDiv
        as="section"
        className="flex min-h-0 flex-1 flex-col rounded-[18px] border border-gray-400 bg-[#f8f9fb] px-[1.5vw] py-[2vh]"
      >
        <DMDiv className="mb-[2.4vh] flex shrink-0 items-center justify-between gap-[2vw]">
          <DMDiv className="flex h-[7vh] items-center rounded-full border border-gray-300 bg-white p-[0.2vh]">
            {(Object.keys(tabConfig) as SendWishesTab[]).map(tab => {
              const Icon = tabConfig[tab].icon;
              const isActive = activeTab === tab;

              return (
                <DMButton
                  key={tab}
                  type="button"
                  onClick={() => handleTabChange(tab)}
                  className={`flex h-[5.8vh] min-w-[13.1vw] items-center justify-center gap-[0.6vw] rounded-full px-[1.5vw] text-[1.75vh] font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-main text-white'
                      : 'bg-white text-primary-main hover:bg-primary-10'
                  }`}
                >
                  {isActive && (isLoading || isRefreshing) ? (
                    <Loader2
                      className="h-[2.4vh] w-[2.4vh] animate-spin"
                      strokeWidth={2.1}
                    />
                  ) : (
                    <Icon className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.1} />
                  )}
                  <DMText as="span">{tabConfig[tab].label}</DMText>
                </DMButton>
              );
            })}
          </DMDiv>

          <DMDiv className="flex items-center gap-[0.8vw]">
            <DMButton
              type="button"
              onClick={handleToggleAll}
              disabled={isLoading || isRefreshing || !rows.length}
              className="rounded-full border border-primary-main bg-white px-[1.8vw] py-[1vh] text-[1.75vh] font-medium text-primary-main transition-colors hover:bg-primary-10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {areAllVisibleRowsSelected ? 'Remove All' : 'Select All'}
            </DMButton>
            <DMButton
              type="button"
              onClick={handleSaveSchedule}
              disabled={isLoading || isRefreshing || isSaving}
              className="rounded-full bg-primary-main px-[2.4vw] py-[1vh] text-[1.75vh] font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving ? 'Saving...' : 'Save & Schedule'}
            </DMButton>
          </DMDiv>
        </DMDiv>

        <PaginatedCardGrid
          className="border-0 bg-transparent px-[0.8vw] pb-[1.2vh] pt-0"
          contentClassName={`overflow-x-hidden px-[0.9vw] py-[1.8vh] pr-[1.2vw] transition-opacity duration-200 ${
            isRefreshing ? 'pointer-events-none opacity-60' : 'opacity-100'
          }`}
          emptyMessage={currentTabConfig.emptyText}
          hasItems={rows.length > 0}
          isLoading={isLoading}
          itemLabel={activeTab === 'FESTIVAL' ? 'wish' : 'invitation'}
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalItems={totalRows}
          onPageIndexChange={setPageIndex}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 20, 30]}
          renderSkeleton={index => <SendWishesCardSkeleton key={index} />}
        >
          {rows.map(item => (
            <SendWishesCard
              key={`${activeTab}-${item.id}`}
              item={item}
              isSelected={selectedEventIds.includes(item.id)}
              onToggle={() => handleToggleCard(item.id)}
            />
          ))}
        </PaginatedCardGrid>
      </DMDiv>
    </DMDiv>
  );
}
